import { RenderedContent } from '../../utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { InputHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { Description } from './fieldset';
import { cn } from '@workspace/utils';

const checkboxVariants = cva(
  [
    'peer relative appearance-none shrink-0 w-5 h-5 rounded-md cursor-pointer',
    'transition-all duration-200 ease-in-out',
    'focus:outline-none focus:ring-offset-0 focus:ring-2',
    'disabled:cursor-not-allowed',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-checkbox-bg border border-checkbox-border',
          'focus:ring-primary/20',
          'checked:bg-checkbox-checked-bg checked:border-checkbox-checked-border',
          'disabled:bg-neutral-100 disabled:border-neutral-300',
        ],
        success: [
          'bg-checkbox-bg border border-checkbox-border',
          'focus:ring-success/20',
          'checked:bg-success checked:border-success',
          'disabled:bg-neutral-100 disabled:border-neutral-300',
        ],
        error: [
          'bg-checkbox-bg border border-checkbox-border',
          'focus:ring-error/20',
          'checked:bg-error checked:border-error',
          'disabled:bg-neutral-100 disabled:border-neutral-300',
        ],
      },
      size: {
        sm: 'w-4 h-4',
        default: 'w-5 h-5',
        lg: 'w-6 h-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const checkmarkVariants = cva(
  [
    'absolute pointer-events-none stroke-white outline-none',
    'opacity-0 peer-checked:opacity-100 transition-opacity duration-200',
  ],
  {
    variants: {
      size: {
        sm: 'w-3 h-3',
        default: 'w-4 h-4',
        lg: 'w-5 h-5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const containerVariants = cva(
  'flex gap-4 transition-all duration-200 bg-checkbox-bg border border-primary-200 rounded-lg p-4',
  {
    variants: {
      alignment: {
        start: 'items-start',
        center: 'items-center',
        end: 'items-end',
      },
      spacing: {
        compact: 'gap-2',
        default: 'gap-4',
        spacious: 'gap-6',
      },
    },
    defaultVariants: {
      alignment: 'center',
      spacing: 'default',
    },
  }
);

const labelVariants = cva(
  [
    'cursor-pointer block transition-colors duration-200',
    'select-none text-[13px]',
  ],
  {
    variants: {
      size: {
        sm: 'text-xs',
        default: 'text-xs',
        lg: 'text-sm',
      },
      state: {
        default: 'text-foreground',
        disabled: 'text-neutral-400 cursor-not-allowed',
      },
    },
    defaultVariants: {
      size: 'default',
      state: 'default',
    },
  }
);

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof checkboxVariants> {
  name: string;
  label: RenderedContent;
  description?: RenderedContent;
  alignment?: 'start' | 'center' | 'end';
  spacing?: 'compact' | 'default' | 'spacious';
  error?: boolean;
  success?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({
     id,
     name,
     label,
     description,
     required,
     className,
     variant = 'default',
     size = 'default',
     alignment = 'center',
     spacing = 'default',
     disabled,
     error,
     success,
     ...props
   }, ref) => {
    // Logique pour déterminer la variante basée sur les états
    const computedVariant = error ? 'error' : success ? 'success' : variant || 'default';
    return (
      // why: tout le bloc est cliquable (le conteneur est le label de la case)
      <label
        htmlFor={id}
        className={cn(containerVariants({ alignment, spacing }), disabled ? 'cursor-not-allowed' : 'cursor-pointer', className)}
      >
        <div className="relative flex items-center justify-center">
          <input
            className={checkboxVariants({ variant: computedVariant, size })}
            id={id}
            name={name}
            ref={ref}
            type="checkbox"
            disabled={disabled}
            {...props}
          />

          <svg
            className={checkmarkVariants({ size })}
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <div className="select-none space-y-1 flex-1">
          <span
            className={labelVariants({
              size,
              state: disabled ? 'disabled' : 'default'
            })}
          >
            {label}
            {required && <span className="text-error ml-1">*</span>}
          </span>

          {description && (
            <Description className={disabled ? 'text-neutral-400' : undefined}>
              {description}
            </Description>
          )}
        </div>
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';
